import { CalendarDays, Download } from 'lucide-react'
import { toast } from 'sonner'

import { useDownloadReport } from '@/hooks/features/reports'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import type { ReportExportName, ReportRangeQuery } from '@/types/reports'

import type { RangePreset } from './useReportRange'
import { Spinner } from '@/components/ui/spinner'

const PRESETS: { value: RangePreset; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom dates' },
]

/** Shopify's grey date-range pill buttons. */
const PILL = 'h-7 w-auto gap-1.5 rounded-full border-0 bg-fill px-3 text-xs font-medium'

interface ReportToolbarProps {
  preset: RangePreset
  range: ReportRangeQuery
  onPresetChange: (preset: RangePreset) => void
  onRangeChange: (range: ReportRangeQuery) => void
  /** Excel downloads offered for this range. */
  downloads?: { name: ReportExportName; label: string }[]
  /** Hide the date controls (reports of the current state, e.g. stock). */
  noRange?: boolean
}

/** Date range (presets or two dates, both days included) and Excel downloads for a report page. */
export const ReportToolbar = ({ preset, range, onPresetChange, onRangeChange, downloads = [], noRange }: ReportToolbarProps) => {
  const download = useDownloadReport()
  const invalid = range.from_date > range.to_date

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {!noRange && (
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="report-preset" className="sr-only">
            Dates
          </Label>
          <Select value={preset} onValueChange={(v) => onPresetChange(v as RangePreset)}>
            <SelectTrigger id="report-preset" className={PILL}>
              <CalendarDays className="size-4 shrink-0" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === 'custom' ? (
            <>
              <Label htmlFor="report-from" className="sr-only">
                From
              </Label>
              <Input id="report-from" type="date" value={range.from_date} max={range.to_date} onChange={(e) => e.target.value && onRangeChange({ ...range, from_date: e.target.value })} className={PILL} />
              <span className="text-xs text-muted-foreground" aria-hidden="true">
                to
              </span>
              <Label htmlFor="report-to" className="sr-only">
                To
              </Label>
              <Input id="report-to" type="date" value={range.to_date} min={range.from_date} onChange={(e) => e.target.value && onRangeChange({ ...range, to_date: e.target.value })} className={PILL} />
            </>
          ) : (
            <span className="text-xs tabular-nums text-muted-foreground">
              {range.from_date} – {range.to_date}
            </span>
          )}
          {invalid && <p className="w-full text-sm text-error">The start date must be on or before the end date.</p>}
        </div>
      )}
      {downloads.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {downloads.map((d) => (
            <Button
              key={d.name}
              variant="outline"
              disabled={download.isPending || invalid}
              onClick={() =>
                download.mutate({ name: d.name, range }, { onError: (e) => toast.error(e.message), onSuccess: () => toast.success(`${d.label} downloaded`) })
              }
            >
              {download.isPending && download.variables?.name === d.name ? <Spinner /> : <Download />}
              {d.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}
